import { useEffect, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { IconGripVertical, IconInfoCircle, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import type { ProjectTemplateTaskDto } from '@ledokol/contracts'
import {
  addTemplateTask,
  deleteTemplateTask,
  fetchTemplates,
  updateTemplateTask,
} from '../../shared/api/templates'

const ROLE_OPTIONS = [
  { value: '', label: 'Не задана' },
  { value: 'account', label: 'Аккаунт' },
  { value: 'employee', label: 'Сотрудник' },
  { value: 'head', label: 'Руководитель' },
]
const ROLE_LABEL: Record<string, string> = {
  account: 'Аккаунт', employee: 'Сотрудник', head: 'Руководитель', director: 'Директор', admin: 'Админ',
}
const PRIORITIES = [
  { value: 'low', label: 'Низкий' },
  { value: 'med', label: 'Средний' },
  { value: 'high', label: 'Высокий' },
  { value: 'urgent', label: 'Срочный' },
]
const PRIO_LABEL: Record<string, string> = { low: 'Низкий', med: 'Средний', high: 'Высокий', urgent: 'Срочный' }
const PRIO_COLOR: Record<string, string> = { low: 'gray', med: 'blue', high: 'orange', urgent: 'red' }

interface Props {
  templateId: string | null
  opened: boolean
  onClose: () => void
  isAdmin: boolean
}

export function TemplateDetailModal({ templateId, opened, onClose, isAdmin }: Props) {
  // Читаем из того же кэша ['templates'] — после мутаций задач список реактивно обновляется
  const templatesQ = useQuery({ queryKey: ['templates'], queryFn: fetchTemplates })
  const template = templatesQ.data?.find((t) => t.id === templateId) ?? null

  const qc = useQueryClient()
  const [taskFormOpen, setTaskFormOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<ProjectTemplateTaskDto | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ProjectTemplateTaskDto | null>(null)

  const removeTask = useMutation({
    mutationFn: (id: string) => deleteTemplateTask(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
      notifications.show({ color: 'teal', title: 'Удалено', message: 'Пункт удалён из шаблона' })
      setDeleteTarget(null)
    },
    onError: () => notifications.show({ color: 'red', title: 'Ошибка', message: 'Не удалось удалить' }),
  })

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={
        <Group gap="xs">
          <Text fw={700}>{template?.name ?? 'Шаблон'}</Text>
          {template && (
            <Badge color={template.is_active ? 'teal' : 'gray'} variant="light" size="sm">
              {template.is_active ? 'Активен' : 'Выключен'}
            </Badge>
          )}
        </Group>
      }
    >
      <Stack gap="md">
        {/* Контекст-подпись: связь с разворачиванием при создании проекта type=new */}
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />}>
          Эти пункты разворачиваются в задачи при создании проекта типа{' '}
          <b>«Новый клиент»</b> (type=new). Здесь вы редактируете не абстрактный список, а будущие
          задачи нового рекламодателя — в той же последовательности.
        </Alert>

        {template?.description && (
          <Text size="sm" c="dimmed">
            {template.description}
          </Text>
        )}

        <Group justify="space-between">
          <Text fw={600} size="sm">
            Пункты шаблона · {template?.tasks.length ?? 0}
          </Text>
          {isAdmin && template && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                setEditingTask(null)
                setTaskFormOpen(true)
              }}
            >
              Добавить пункт
            </Button>
          )}
        </Group>

        <Stack gap="xs">
          {template?.tasks.map((t, i) => (
            <Group
              key={t.id}
              justify="space-between"
              wrap="nowrap"
              p="sm"
              style={{ border: '1px solid var(--mantine-color-gray-2)', borderRadius: 8 }}
            >
              <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                <IconGripVertical size={16} color="var(--mantine-color-gray-4)" />
                <Text size="sm" c="dimmed" fw={600} w={20}>
                  {i + 1}
                </Text>
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text size="sm" fw={500} style={{ whiteSpace: 'normal' }}>
                    {t.title}
                  </Text>
                  <Group gap={6}>
                    <Badge size="xs" variant="dot" color={PRIO_COLOR[t.priority]}>
                      {PRIO_LABEL[t.priority]}
                    </Badge>
                    {t.default_assignee_role && (
                      <Badge size="xs" variant="light" color="blue">
                        {ROLE_LABEL[t.default_assignee_role] ?? t.default_assignee_role}
                      </Badge>
                    )}
                    {t.due_offset_days != null && (
                      <Text size="xs" c="dimmed">
                        +{t.due_offset_days} дн.
                      </Text>
                    )}
                  </Group>
                </Stack>
              </Group>
              {isAdmin && (
                <Group gap={4} wrap="nowrap">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => {
                      setEditingTask(t)
                      setTaskFormOpen(true)
                    }}
                  >
                    <IconPencil size={16} />
                  </ActionIcon>
                  <ActionIcon variant="subtle" color="red" onClick={() => setDeleteTarget(t)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              )}
            </Group>
          ))}
          {template && template.tasks.length === 0 && (
            <Text c="dimmed" size="sm" py="md" ta="center">
              В шаблоне пока нет пунктов
            </Text>
          )}
        </Stack>
      </Stack>

      {template && (
        <TemplateTaskForm
          templateId={template.id}
          opened={taskFormOpen}
          onClose={() => setTaskFormOpen(false)}
          editing={editingTask}
        />
      )}

      <Modal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={<Text fw={700}>Удалить пункт?</Text>}
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">«{deleteTarget?.title}» будет удалён из шаблона.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>
              Отмена
            </Button>
            <Button color="red" loading={removeTask.isPending} onClick={() => deleteTarget && removeTask.mutate(deleteTarget.id)}>
              Удалить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  )
}

function TemplateTaskForm({
  templateId,
  opened,
  onClose,
  editing,
}: {
  templateId: string
  opened: boolean
  onClose: () => void
  editing: ProjectTemplateTaskDto | null
}) {
  const qc = useQueryClient()
  const [title, setTitle] = useState('')
  const [role, setRole] = useState('')
  const [priority, setPriority] = useState('med')
  const [description, setDescription] = useState('')
  const [dueDays, setDueDays] = useState<number | ''>('')

  useEffect(() => {
    if (!opened) return
    setTitle(editing?.title ?? '')
    setRole(editing?.default_assignee_role ?? '')
    setPriority(editing?.priority ?? 'med')
    setDescription(editing?.description ?? '')
    setDueDays(editing?.due_offset_days ?? '')
  }, [opened, editing])

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: title.trim(),
        default_assignee_role: role || null,
        priority: priority as ProjectTemplateTaskDto['priority'],
        description: description.trim() || null,
        due_offset_days: dueDays === '' ? null : dueDays,
      }
      return editing ? updateTemplateTask(editing.id, body) : addTemplateTask(templateId, body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] })
      notifications.show({ color: 'teal', title: editing ? 'Пункт обновлён' : 'Пункт добавлен', message: title.trim() })
      onClose()
    },
    onError: () => notifications.show({ color: 'red', title: 'Ошибка', message: 'Не удалось сохранить' }),
  })

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="md"
      title={<Text fw={700}>{editing ? 'Редактировать пункт' : 'Новый пункт шаблона'}</Text>}
    >
      <Stack gap="md">
        <TextInput
          label="Название задачи"
          required
          placeholder="Например: Получить доступ к GA4"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />
        <Group grow>
          <Select label="Исполнитель по роли" data={ROLE_OPTIONS} value={role} onChange={(v) => setRole(v ?? '')} allowDeselect={false} />
          <Select label="Приоритет" data={PRIORITIES} value={priority} onChange={(v) => setPriority(v ?? 'med')} allowDeselect={false} />
        </Group>
        <NumberInput
          label="Срок (дней от создания проекта, необязательно)"
          min={0}
          max={365}
          value={dueDays}
          onChange={(v) => setDueDays(typeof v === 'number' ? v : '')}
        />
        <Textarea
          label="Описание (необязательно)"
          autosize
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={title.trim().length === 0}>
            {editing ? 'Сохранить' : 'Добавить'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
